import type { Rule, RuleFiring, DocumentSnapshot } from "../types";

/**
 * BW10 — fires when any document expires within 90 days. Severity tiers:
 *   - bad  : ≤30 days
 *   - warn : 31-90 days
 *
 * The firing message names the most-imminent document. Action route:
 * /documents/:id so the AlertBanner can deep-link.
 */

export const documentExpiringSoon: Rule = {
  id: "document_expiring_soon",
  domain: "identity",
  topics: ["wallet", "document", "expiry"],
  evidence_refs: [],
  title: () => "Document expiring",
  evaluate(ctx): RuleFiring | null {
    const docs: DocumentSnapshot[] = ctx.documents ?? [];
    if (docs.length === 0) return null;

    const now = ctx.now;
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let earliest: { d: DocumentSnapshot; days: number } | null = null;

    for (const d of docs) {
      if (!d.expires_at) continue;
      const target = new Date(`${d.expires_at}T00:00:00`);
      const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
      if (days < 0) continue; // already expired — separate rule could surface this
      if (days > 90) continue;
      if (!earliest || days < earliest.days) earliest = { d, days };
    }
    if (!earliest) return null;

    const severity = earliest.days <= 30 ? "bad" : "warn";
    const message =
      earliest.days <= 0
        ? `${earliest.d.title} expires today`
        : `${earliest.d.title} expires in ${earliest.days}d`;

    return {
      rule_id: "document_expiring_soon",
      ts: now.toISOString(),
      tz: ctx.tz,
      severity,
      message,
      evidence_refs: [],
      factors: [
        {
          name: "days_until_expiry",
          value: earliest.days,
          contribution: severity === "bad" ? "≤30 days → critical" : "31-90 days → warn",
          evidence_ref: "",
        },
      ],
      target_surface: `/documents/${earliest.d.id}`,
      target_action: { kind: "open_document", params: { id: earliest.d.id } },
    };
  },
};
